import { upperFirst } from 'lodash';
import { FILE_MIME } from './constants';
import { getFileExtension } from './utils';

export const FILE_FORMATS = ['pdf', 'word', 'excel', 'image'] as const;

export type FileFormat = (typeof FILE_FORMATS)[number];

export interface AttachmentSettings {
  maxFileSizeMb?: number;
  allowedFormats?: FileFormat[];
}

export interface AttachedFile {
  fileId: string;
  name: string;
  size: number;
  mimeType: string;
}

export const FILE_MESSAGES = {
  wrongFormat: (formats: string) => `Only ${formats} files can be attached.`,
  tooMany: (max: number) => (max === 1 ? 'Only one file can be attached.' : `At most ${max} files can be attached.`),
  notAttached: (name: string, problem: string) => `“${name}” wasn't attached. ${problem}`,
};

export const UNKNOWN_FILE_TYPE = 'application/octet-stream';

const EXTENSIONS: Record<FileFormat, string[]> = {
  pdf: [FILE_MIME.PDF.ext],
  word: [FILE_MIME.DOC.ext, FILE_MIME.DOCX.ext],
  excel: [FILE_MIME.XLS.ext, FILE_MIME.XLSX.ext],
  image: [FILE_MIME.JPG.ext, FILE_MIME.JPEG.ext, FILE_MIME.PNG.ext],
};

export const FILE_FORMAT_LABELS: Record<FileFormat, string[]> = {
  pdf: ['PDF'],
  word: ['Word'],
  excel: ['Excel'],
  image: ['JPG', 'PNG'],
};

export const allowedFormatsOf = (settings: AttachmentSettings): FileFormat[] => {
  return FILE_FORMATS.filter(format => settings.allowedFormats?.includes(format));
};

export const acceptOf = (settings: AttachmentSettings): string | undefined => {
  const extensions = allowedFormatsOf(settings).flatMap(format => EXTENSIONS[format]);
  return extensions.length > 0 ? extensions.map(extension => `.${extension}`).join(',') : undefined;
};

export const fileProblem = (settings: AttachmentSettings, file: { name: string; size: number }) => {
  const formats = allowedFormatsOf(settings);
  const extension = getFileExtension(file.name);
  if (formats.length > 0 && !formats.some(format => EXTENSIONS[format].includes(extension)))
    return FILE_MESSAGES.wrongFormat(joinWords(formatLabelsOf(formats), 'and'));
  return undefined;
};

export const limitsText = (
  settings: AttachmentSettings,
  bounds: { min?: { value: number | string }; max?: { value: number | string } },
): string => {
  const parts: string[] = [];
  const formats = allowedFormatsOf(settings);
  if (formats.length > 0) parts.push(joinWords(formatLabelsOf(formats), 'or'));
  const min = typeof bounds.min?.value === 'number' ? bounds.min.value : 0;
  const max = typeof bounds.max?.value === 'number' ? bounds.max.value : undefined;
  const count = countText(min, max);
  if (count) parts.push(count);
  return upperFirst(parts.join(' · '));
};

const countText = (min: number, max: number | undefined): string | undefined => {
  if (max === undefined) return min >= 2 ? `at least ${min} files` : undefined;
  if (max === 0) return 'no files';
  if (min >= 2) return min >= max ? `${max} files` : `${min} to ${max} files`;
  return max === 1 ? 'one file' : `up to ${max} files`;
};

const formatLabelsOf = (formats: FileFormat[]): string[] => {
  return formats.flatMap(format => FILE_FORMAT_LABELS[format]);
};

const joinWords = (items: string[], word: 'and' | 'or'): string => {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} ${word} ${items.at(-1)}`;
};
